import { action, computed, flow, makeObservable, observable } from 'mobx';

import { assert } from '../shared/utils/assert';
import type { IDataSource } from '../template-builder/TemplateBuilder';

import { FavoritesApi } from './api/Favorites.api';
import type { IFavoritesService } from './IFavoritesService';

export class FavoritesDataSource implements IDataSource {
  constructor(private readonly source: { [key: string]: number[] }) {}

  hasKey(key: string): boolean {
    return key in this.source;
  }

  getValue(key: string) {
    assert(key in this.source);

    const value = this.source[key];

    assert(typeof value !== 'undefined');

    return value;
  }
}

function isFavoritesResponse(favorites: unknown): favorites is number[] {
  if (!favorites || (!!favorites && typeof favorites !== 'object')) return false;
  if (!Array.isArray(favorites)) return false;

  for (const favoriteItem of favorites) {
    if (typeof favoriteItem !== 'number') {
      return false;
    }
  }

  return true;
}

export class FavoritesWellsService implements IFavoritesService {
  @observable wells: number[] = [];
  @observable hasError: boolean = false;
  @observable favoritesObjectId?: number;

  private readonly api = new FavoritesApi();

  constructor() {
    makeObservable(this);
  }

  get wellIds(): number[] {
    return this.wells;
  }

  @computed
  get dataSource(): IDataSource {
    return new FavoritesDataSource({ favoriteWellIds: this.wellIds });
  }

  hasWell(wellId: number): boolean {
    return !!this.wells.includes(wellId);
  }

  resetError(): void {
    this.hasError = false;
  }

  @action.bound
  setList(list: number[]): void {
    this.wells = list;
  }

  @flow.bound
  async *loadFavoritesIds() {
    try {
      const favoritesResponse = await this.api.getFavorites();

      yield;

      if (favoritesResponse.length === 0) {
        const newFavoritesObject = await this.api.createFavotites();

        yield;

        this.favoritesObjectId = newFavoritesObject.id;
        this.wells = [];
      } else {
        const {
          data: { favorites },
          id,
        } = favoritesResponse[0];

        this.favoritesObjectId = id;
        this.wells = isFavoritesResponse(favorites) ? favorites : [];
      }
    } catch (error) {
      yield;

      console.error(error);
      this.hasError = true;
    }
  }

  @flow.bound
  async *disableFavorite(wellId: number) {
    if (!this.favoritesObjectId) return;

    this.hasError = false;

    try {
      const filteredList = this.wellIds.filter((_wellId) => _wellId !== wellId);
      await this.api.updateFavorites(this.favoritesObjectId, filteredList);

      yield;

      this.wells.remove(wellId);
    } catch (error) {
      yield;

      console.error(error);
      this.hasError = true;
    }
  }

  @flow.bound
  async *enableFavorite(wellId: number) {
    if (!this.favoritesObjectId) return;

    this.hasError = false;

    try {
      const newWells = [...this.wellIds, wellId];

      await this.api.updateFavorites(this.favoritesObjectId, newWells);

      yield;

      this.wells.push(wellId);
    } catch (error) {
      yield;

      console.error(error);
      this.hasError = true;
    }
  }
}
